import {
  CRM_JSON_MAX_CHARS,
  MAX_IMAGES,
  OLLAMA_BASE_URL,
  OLLAMA_BEARER,
  OLLAMA_MODEL,
  OLLAMA_TIMEOUT_MS,
} from './config'
import { COMPARE_FIELDS, ExtractedFields } from './compare'

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onerror = () => reject(r.error)
    r.onloadend = () => {
      if (typeof r.result === 'string') resolve(r.result)
      else reject(new Error('blobToDataUrl: unexpected result type'))
    }
    r.readAsDataURL(blob)
  })

const fetchImagesAsBase64 = async (urls: string[]): Promise<string[]> => {
  const picked = urls.slice(0, MAX_IMAGES)
  const out: string[] = []
  for (const u of picked) {
    const res = await fetch(u, { credentials: 'include' })
    if (!res.ok) {
      throw new Error(`Image fetch failed ${u}: ${res.status}`)
    }
    const blob = await res.blob()
    const dataUrl = await blobToDataUrl(blob)
    const m = /^data:[^;]+;base64,(.+)$/i.exec(dataUrl)
    if (!m) {
      throw new Error('unexpected data URL shape from image')
    }
    out.push(m[1])
  }
  return out
}

const buildPrompt = (crmItem: Record<string, unknown>, imageCount: number): string => {
  let crmJsonText = JSON.stringify(crmItem, null, 2)
  let truncatedNote = ''
  if (crmJsonText.length > CRM_JSON_MAX_CHARS) {
    crmJsonText = crmJsonText.slice(0, CRM_JSON_MAX_CHARS)
    truncatedNote = '\n(JSON was truncated for prompt size.)\n'
  }
  const fieldKeys = COMPARE_FIELDS.map((f) => f.key).join(', ')
  const skeleton = `{"is_document":true|false,"summary_en":"short English note","extracted_fields":{${COMPARE_FIELDS.map((f) => `"${f.key}":null`).join(',')}}}`
  return [
    'You are a verification assistant. CRM JSON (applicant-submitted fields):',
    crmJsonText,
    truncatedNote,
    `There are ${imageCount} image(s). They may be different sides or pages of the same ID (e.g. front vs back).`,
    'Fields can be split across images: read EVERY image and merge into ONE extracted_fields object.',
    'If the same field appears on multiple images, values must be consistent; if they conflict, use the clearest reading and say so briefly in summary_en.',
    'Attached images are claimed to be identity documents.',
    'Tasks:',
    '1) Decide if, taken together, the images plausibly show an identity document (passport/ID), not unrelated photos.',
    '2) After scanning all images, fill the same identity fields the CRM uses for the verification record.',
    'STRICT OUTPUT RULES:',
    `- "extracted_fields" MUST contain ALL of these keys, in this exact order: ${fieldKeys}.`,
    '- Never omit a key. If a field is not visible on any image, set its value to null - do not drop the key.',
    '- Values must be strings or null. No numbers, no booleans, no objects, no arrays.',
    '- For dates use YYYY-MM-DD when possible.',
    '- Reply with ONLY valid JSON. No markdown, no code fences, no commentary, no trailing text.',
    'Required JSON shape (use these keys verbatim, fill values):',
    skeleton,
  ].join('\n\n')
}

const parseModelJson = (raw: string): unknown => {
  const trimmed = raw.trim()
  const fence = /^```(?:json)?\s*([\s\S]*?)```$/im.exec(trimmed)
  const inner = fence ? fence[1].trim() : trimmed
  return JSON.parse(inner)
}

export type OllamaResult = {
  llmRaw: string
  parsed: {
    is_document?: boolean
    summary_en?: string
    summary_ru?: string
    extracted_fields?: ExtractedFields
  }
  imageCount: number
}

export const runVerifyWithOllama = async (
  crmItem: Record<string, unknown>,
  imageUrls: string[],
): Promise<OllamaResult> => {
  if (imageUrls.length === 0) {
    throw new Error('No document images to verify.')
  }
  const images = await fetchImagesAsBase64(imageUrls)
  const userText = buildPrompt(crmItem, images.length)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (OLLAMA_BEARER.length > 0) {
    headers.Authorization = `Bearer ${OLLAMA_BEARER}`
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), OLLAMA_TIMEOUT_MS)

  let res: Response
  try {
    res = await fetch(`${OLLAMA_BASE_URL.replace(/\/$/, '')}/api/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model: OLLAMA_MODEL,
        messages: [{ role: 'user', content: userText, images }],
        stream: false,
      }),
      signal: controller.signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error(`Ollama is unreachable (timeout ${OLLAMA_TIMEOUT_MS}ms).`)
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }

  if (!res.ok) {
    const t = await res.text()
    throw new Error(`Ollama ${res.status}: ${t.slice(0, 500)}`)
  }

  const data = await res.json()
  const content = data && data.message && data.message.content
  if (typeof content !== 'string') {
    throw new Error(`Ollama unexpected response: ${JSON.stringify(data).slice(0, 400)}`)
  }

  const parsed = parseModelJson(content) as OllamaResult['parsed']
  return { llmRaw: content, parsed, imageCount: images.length }
}
